import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiCalendar, FiPlus, FiSearch } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SearchBar from 'components/shared/SearchBar';
import SubscriberModal from './SubscriberModal';

interface SubscribersFiltersProps {
  filters: {
    status: string;
    customer: string;
    subscriptionType: string;
    billingPeriod: string;
    paymentStatus: string;
    startDate: Date | null;
    endDate: Date | null;
    search: string;
  };
  setFilters: (filters: any) => void;
}

export default function SubscribersFilters({ filters, setFilters }: SubscribersFiltersProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const handleDropdownClick = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleOptionSelect = (dropdownName: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [dropdownName]: value }));
    setOpenDropdown(null);
};

  const clearFilters = () => {
    setFilters({
      status: '',
      customer: '',
      subscriptionType: '',
      billingPeriod: '',
      paymentStatus: '',
      startDate: null,
      endDate: null,
      search: ''
    });
    setStartDate(null);
    setEndDate(null);
    setSearchTerm('');
  };


  const statuses = ['Active', 'Inactive', 'Pending', 'Cancelled', 'Trial'];
  
  const subscriptionTypes = ['Free Trial', 'Basic', 'Premium', 'Enterprise'];

  const billingPeriod = ['Monthly', 'Quarterly', 'Yearly'];

  const paymentStatus = ['Paid', 'Pending', 'Failed', 'Refunded'];


  const Dropdown = ({  name, options, label  }: { name: string; options: string[]; label: string }) => (
    <div className="relative">
        <button
            onClick={() => handleDropdownClick(name)}
            className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
            style={{
                minWidth: '120px',
                maxWidth: '120px',
            }}
        >
            <span className="truncate mr-2">
            {filters[name as keyof typeof filters] as string || label}
            </span>
            {openDropdown === name ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
        </button>

        {openDropdown === name && (
            <>
                <div
                    className="fixed inset-0 z-10"
                    onClick={() => setOpenDropdown(null)}
                />
                <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
                    <button
                        disabled
                        className="w-full text-left px-4 py-2 text-gray-500 bg-[#2A2A2A] cursor-default truncate opacity-50"
                    >
                        {label}
                    </button>
                    
                    <div className="border-b border-white/[0.08] my-1"></div>

                    {options.map((option,index) => (
                        <button
                            key={index}
                            onClick={() => handleOptionSelect(name, option)}
                            className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white truncate"
                            title={option}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </>
        )}
    </div>
);
  
  return (
    <>
      <div className="flex justify-between items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Date Filter */}
          <div className="relative">
                        <button
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
                            style={{
                                minWidth: '120px',
                                maxWidth: '200px',
                            }}
                        >
                            <div className="flex items-center gap-2 truncate">
                                <FiCalendar className="flex-shrink-0" />
                                <span className="truncate">
                                    {(filters.startDate && filters.endDate)
                                        ? `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
                                        : 'Duration'
                                    }
                                </span>
                            </div>
                        </button>

                        {showDatePicker && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowDatePicker(false)}
                                />
                                <div className="absolute top-full left-0 mt-2 z-20 bg-[#2A2A2A] rounded-lg p-4 border border-white/[0.08]">
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={filters.startDate}
                                        endDate={filters.endDate}
                                        onChange={(update: [Date | null, Date | null]) => {
                                            setFilters((prev: any) => ({
                                                ...prev,
                                                startDate: update[0],
                                                endDate: update[1]
                                            }));
                                            if (update[0] && update[1]) {
                                                setShowDatePicker(false);
                                            }
                                        }}
                                        inline
                                    />
                                </div>
                            </>
                        )}
                    </div>


          <Dropdown
            name="status"
            options={statuses}
            label="Subscription Status"
          />

          <Dropdown
            name="subscriptionType"
            options={subscriptionTypes}
            label="Subscription Type"
          />

          <Dropdown
            name="billingPeriod"
            options={billingPeriod}
            label="Billing Period"
          />

          <Dropdown
            name="paymentStatus"
            options={paymentStatus}
            label="Payment Status"
          />

          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setFilters({ ...filters, search: value });
            }}
            placeholder="Search..."
          />

          {Object.values(filters).some(Boolean) && (
            <button
              onClick={clearFilters}
              className="text-red-400 hover:text-red-300"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
          >
            <FiPlus /> Add Subscriber
          </button>
        </div>
      </div>

      <SubscriberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode="create"
      />
    </>
  );
} 